import { useCallback, useEffect, useState } from 'react';
import {
  Link,
  Outlet,
  useParams,
  useLocation,
  useLoaderData,
  matchPath,
} from 'react-router-dom';
import { Close } from '@mui/icons-material';
import AddIcon from '@mui/icons-material/Add';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import MenuIcon from '@mui/icons-material/Menu';
import {
  Box,
  Button,
  ButtonBase,
  ClickAwayListener,
  Paper,
  Popper,
  Tab,
  Tabs,
  AppBar,
  Toolbar,
  useTheme,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Fab,
  Tooltip,
  Divider,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { SamdAppRepository } from '@samd/api/app_repository';
import { HelpDropdown } from '@samd/features/help';
import { LdtAppRepository } from '@ldt/api/app_repository';
import { AppRepository } from 'src/api/app_repository';
import { AuthenticationService } from 'src/api/authentication_service';
import { OrderRepository } from 'src/api/order_repository';
import DarkLogoIcon from 'src/assets/artera_text_logo_dark.svg';
import LightLogoIcon from 'src/assets/artera_text_logo_light.svg';
import { ProfileDropdown } from 'src/components/profile_dropdown';
import { UploadOverlayManager } from 'src/components/slide_image_upload_overlay';
import { getApp, getPlatform } from 'src/config/environment';
import { isFeatureEnabled, FeatureFlag } from 'src/config/features';
import { useHelpMenu } from 'src/distribution_platforms/features/components/help';
import { OrganizationHeader } from 'src/distribution_platforms/features/components/organization_header';
import {
  useCanCreateOrder,
  useWorkflowType,
} from 'src/distribution_platforms/features/hooks/use_workflow';
import {
  useCanManageMembers,
  useIsLdtSalesOrCustomerSuccessUser,
  useUser,
} from 'src/hooks/use_auth';
import { UploadProvider } from 'src/hooks/use_upload';
import { App } from 'src/types/app';
import { DistributionPlatform } from 'src/types/env';
import { WorkflowType } from 'src/types/workflows';
import { notifyError, notifySuccess } from 'src/utils';

const MainPageContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  height: '100vh',
  width: '100%',
});

const MainLayout = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  flex: 1,
  minHeight: 0,
  height: '100%',
  width: '100%',
});

/** Keep in sync across AppBar, Toolbar, nav tabs, and any layout offset. */
const APP_BAR_HEIGHT_PX = 60;
const APP_BAR_HEIGHT_SM_PX = 56;

const StyledAppBar = styled(AppBar)(({ theme }) => ({
  flexShrink: 0,
  width: 'min(100%, 2600px)',
  height: APP_BAR_HEIGHT_PX,
  zIndex: theme.zIndex.appBar,
  backgroundColor:
    theme.palette.mode === 'dark'
      ? theme.palette.grey[600]
      : theme.palette.common.white,
  alignContent: 'center',
  padding: '0 30px',
  boxShadow: '0px 4px 18px 0px rgba(0, 0, 0, 0.06)',
  color: theme.palette.text.primary,
  [theme.breakpoints.down('md')]: {
    padding: '0 20px',
  },
  [theme.breakpoints.down('sm')]: {
    padding: '0 16px',
    height: APP_BAR_HEIGHT_SM_PX,
  },
}));

const StyledToolbar = styled(Toolbar)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  padding: '0 !important',
  height: APP_BAR_HEIGHT_PX,
  minHeight: `${APP_BAR_HEIGHT_PX}px !important`,
  maxHeight: APP_BAR_HEIGHT_PX,
  [theme.breakpoints.down('sm')]: {
    height: APP_BAR_HEIGHT_SM_PX,
    minHeight: `${APP_BAR_HEIGHT_SM_PX}px !important`,
    maxHeight: APP_BAR_HEIGHT_SM_PX,
  },
}));

const LogoImage = styled('img')(({ theme }) => ({
  height: 22,
  width: 137,
  marginRight: '40px',
  cursor: 'pointer',
  [theme.breakpoints.down('lg')]: {
    height: 18,
    width: 110,
    marginRight: '12px',
  },
}));

const DemoPortalBannerBar = styled(Box)(({ theme }) => ({
  alignSelf: 'stretch',
  flexShrink: 0,
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxSizing: 'border-box',
  padding: theme.spacing(1, 2),
  backgroundColor: theme.palette.error.main,
  color: theme.palette.error.contrastText,
}));

const PageLayout = styled(Box)(({ theme }) => ({
  flex: 1,
  minHeight: 0,
  width: 'min(100%, 2600px)',
  padding: '16px 40px 8px 40px',
  position: 'relative',
  backgroundColor: theme.palette.grey[100],
  overflow: 'auto',
  display: 'flex',
  flexDirection: 'column',
  [theme.breakpoints.down('md')]: {
    padding: '16px 20px 8px 20px',
  },
  [theme.breakpoints.down('sm')]: {
    padding: '8px',
  },
  scrollbarGutter: 'stable both-edges',
}));

const DemoPortalBanner = () => (
  <DemoPortalBannerBar role='banner' aria-label='Demo portal notice'>
    <Typography
      variant='h5'
      sx={{
        fontWeight: 600,
        textAlign: 'center',
      }}
    >
      Demo Portal Only - NOT For Clinical Use
    </Typography>
  </DemoPortalBannerBar>
);

// Style the Avatar circle using MUI's styled API
const AvatarCircle = styled(Box)(({ theme }) => ({
  width: 40,
  height: 40,
  borderRadius: '50%',
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: theme.typography.h6.fontSize,
  fontWeight: 'bold',
}));

// Profile Header Component
const ProfileHeader: React.FC = () => {
  const user = useUser();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl((prev) => (prev ? null : event.currentTarget));
  };

  const handleClose = (event: MouseEvent | TouchEvent) => {
    if (
      anchorEl &&
      event.target instanceof Node &&
      anchorEl.contains(event.target)
    ) {
      return; // Click was on the profile button; let its onClick handle the toggle
    }
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const dropdownId = open ? 'profile-dropdown' : undefined;

  return (
    <Box
      sx={{
        position: 'relative',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          cursor: 'pointer',
        }}
      >
        <ButtonBase
          aria-label='Profile menu'
          aria-describedby={dropdownId}
          onClick={handleClick}
        >
          <AvatarCircle>{user?.name?.charAt(0).toUpperCase()}</AvatarCircle>
          {!open && <ArrowDropDownIcon />}
          {open && <Close />}
        </ButtonBase>
      </Box>
      {/* Use Popper instead of Popover to allow user to interact with background content. */}
      <Popper
        id={dropdownId}
        open={open}
        anchorEl={anchorEl}
        placement='bottom-start'
        modifiers={[
          {
            name: 'offset',
            options: {
              offset: [0, 8],
            },
          },
        ]}
        style={{ zIndex: 1300 }}
      >
        <ClickAwayListener onClickAway={handleClose}>
          <Paper sx={{ mt: 1, borderRadius: '10px', boxShadow: 3 }}>
            <ProfileDropdown />
          </Paper>
        </ClickAwayListener>
      </Popper>
    </Box>
  );
};

const Header = ({
  organization,
  logo,
  appRepository,
  loaderData,
}: {
  organization: string;
  logo: string;
  appRepository: AppRepository;
  loaderData: unknown;
}) => {
  const location = useLocation();
  const canManageMembers = useCanManageMembers();
  const isCSOrSalesUser = useIsLdtSalesOrCustomerSuccessUser();
  const workflowType = useWorkflowType();
  const showCreateOrderButton = useCanCreateOrder();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const {
    anchorEl: helpAnchorEl,
    isOpen: isHelpMenuOpen,
    dropdownId: helpDropdownId,
    openMenu: openHelpMenu,
    closeMenu: closeHelpMenu,
  } = useHelpMenu();

  // Define all possible tabs
  const allTabs = [
    { id: 0, label: 'Orders' },
    { id: 1, label: 'Members' },
    { id: 2, label: 'Help' },
  ];

  // Filter tabs based on user role and feature flags
  const tabs = allTabs.filter((tab) => {
    if (tab.id === 1) {
      // Members tab - only show for users who can manage members
      if (workflowType === WorkflowType.TEMPUS) {
        return isCSOrSalesUser;
      }
      return canManageMembers;
    } else if (tab.id === 2) {
      // Help tab - only show when feature is enabled
      return isFeatureEnabled(FeatureFlag.HelpTab);
    }

    return true; // Show all other tabs
  });

  const getPathForTab = (tabId: number) => {
    switch (tabId) {
      case 0:
        return `/${organization}/orders`;
      case 1:
        return `/${organization}/members`;
      default:
        return undefined;
    }
  };

  const handleHelpTabClick = (event: React.MouseEvent<HTMLElement>) => {
    openHelpMenu(event);
  };

  const handleMobileMenuToggle = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  // Get active tab based on current route
  const getActiveTab = useCallback(() => {
    // If there's a background location (modal is open), use that for determining active tab
    const backgroundLocation = location.state?.background as
      | { pathname: string; search: string }
      | undefined;
    const pathToCheck = backgroundLocation?.pathname || location.pathname;

    // Use matchPath for robust route matching
    const membersMatch = matchPath(
      { path: '/:organization/members', end: true },
      pathToCheck,
    );
    const invitesMatch = matchPath(
      { path: '/:organization/invites', end: true },
      pathToCheck,
    );

    if (membersMatch || invitesMatch) {
      // Find the Members tab in the filtered tabs array
      const membersTabIndex = tabs.findIndex((tab) => tab.id === 1);
      return membersTabIndex !== -1 ? membersTabIndex : 0; // Fallback to Orders if Members not found
    }

    return 0; // Orders tab (always at index 0)
  }, [location.pathname, location.state?.background, tabs]);

  return (
    <StyledAppBar position='static' elevation={0}>
      <StyledToolbar>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
          }}
        >
          {/* Hamburger menu for mobile */}
          <IconButton
            edge='start'
            onClick={handleMobileMenuToggle}
            sx={{
              mr: 2,
              display: { xs: 'flex', md: 'none' },
              color: 'inherit',
              width: 40,
              height: 40,
              borderRadius: '50%',
              justifyContent: 'center',
              alignItems: 'center',
              '&:hover': {
                backgroundColor: 'action.hover',
              },
            }}
          >
            <MenuIcon />
          </IconButton>

          <Link
            to={`/${organization}/orders`}
            style={{
              display: 'flex',
              alignItems: 'center',
              textDecoration: 'none',
            }}
          >
            <LogoImage src={logo} alt='Artera Logo' />
          </Link>

          {/* Hide navigation tabs on mobile */}
          <Box
            sx={{
              position: 'relative',
              display: { xs: 'none', md: 'block' },
            }}
          >
            <NavigationTabs
              tabs={tabs}
              organization={organization}
              activeTab={getActiveTab()}
              onHelpTabClick={handleHelpTabClick}
            />
            {getPlatform() === DistributionPlatform.Samd && (
              <HelpDropdown
                id={helpDropdownId}
                open={isHelpMenuOpen}
                anchorEl={helpAnchorEl}
                onClose={closeHelpMenu}
                appRepository={appRepository as SamdAppRepository}
                loaderData={loaderData}
              />
            )}
          </Box>
        </Box>
        <Box
          sx={{
            display: 'flex',
            gap: { xs: 1, sm: 2 },
            alignItems: 'center',
          }}
        >
          {/* Organization Header - for LDT platform users */}
          {getPlatform() === DistributionPlatform.Ldt && (
            <OrganizationHeader
              appRepository={appRepository as LdtAppRepository}
              authenticationService={AuthenticationService.getInstance()}
            />
          )}
          {/* CREATE ORDER button - hidden on small screens, shown on medium+ */}
          {showCreateOrderButton && (
            <Button
              component={Link}
              to={`/${organization}/orders/new`}
              state={{
                background: {
                  pathname: location.pathname,
                  search: location.search,
                },
                isNewOrder: true,
              }}
              variant='contained'
              size='large'
              sx={{
                display: { xs: 'none', sm: 'flex' },
                whiteSpace: 'nowrap',
              }}
            >
              CREATE ORDER
            </Button>
          )}
          <ProfileHeader />
        </Box>
      </StyledToolbar>
      {/* Mobile Navigation Drawer */}
      <Drawer
        anchor='left'
        open={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        sx={{
          display: { xs: 'block', lg: 'none' },
          '& .MuiDrawer-paper': {
            width: 250,
            boxSizing: 'border-box',
          },
        }}
      >
        <List>
          {/* Organization Header for mobile - only for LDT platform */}
          {getPlatform() === DistributionPlatform.Ldt && (
            <Box
              sx={{
                // For clinicians: show on xs/sm only (desktop shows on md+)
                // For CS/Sales: show on xs/sm only (desktop shows on md+)
                display: { xs: 'block', sm: 'block', md: 'none' },
              }}
            >
              <Box
                sx={{
                  px: 2,
                  py: 1.5,
                }}
              >
                <OrganizationHeader
                  appRepository={appRepository as LdtAppRepository}
                  authenticationService={AuthenticationService.getInstance()}
                  onMobileDrawerClose={() => setMobileMenuOpen(false)}
                  variant='drawer'
                />
              </Box>
              <Divider />
            </Box>
          )}
          {tabs.map((tab) => {
            const path = getPathForTab(tab.id);
            if (path) {
              return (
                <ListItem key={tab.id} disablePadding>
                  <ListItemButton
                    component={Link}
                    to={path}
                    onClick={() => setMobileMenuOpen(false)}
                    sx={{ py: 2 }}
                  >
                    <ListItemText primary={tab.label} />
                  </ListItemButton>
                </ListItem>
              );
            }
            return (
              <ListItem key={tab.id} disablePadding>
                <ListItemButton
                  onClick={(event) => handleHelpTabClick(event)}
                  sx={{ py: 2 }}
                >
                  <ListItemText primary={tab.label} />
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
      </Drawer>
    </StyledAppBar>
  );
};

// Main Layout Component
export const Layout: React.FC<{
  appRepository: AppRepository;
  orderRepository: OrderRepository;
  uploadWorker: Worker;
}> = ({ appRepository, orderRepository, uploadWorker }) => {
  const { organization: organizationParam } = useParams();
  const theme = useTheme();
  const location = useLocation();
  const showCreateOrderButton = useCanCreateOrder();
  // Select logo based on theme mode
  const logo = theme.palette.mode === 'dark' ? DarkLogoIcon : LightLogoIcon;
  const loaderData = useLoaderData();
  const showDemoPortalBanner = getApp() === App.SamdDemo;

  // Display toast from session storage if it exists, then clear the toast information.
  useEffect(() => {
    const toastType = window.sessionStorage.getItem('toastType');
    const toastMessage = window.sessionStorage.getItem('toastMessage');
    if (toastType && toastMessage) {
      if (toastType === 'error') {
        notifyError(toastMessage);
      } else if (toastType === 'success') {
        notifySuccess(toastMessage);
      }
      window.sessionStorage.removeItem('toastType');
      window.sessionStorage.removeItem('toastMessage');
    }
  }, [location.pathname]);

  return (
    <MainPageContainer>
      <MainLayout>
        <Header
          organization={organizationParam || ''}
          logo={logo}
          appRepository={appRepository}
          loaderData={loaderData}
        />
        {showDemoPortalBanner && <DemoPortalBanner />}
        <UploadProvider
          orderRepository={orderRepository}
          uploadWorker={uploadWorker}
        >
          <PageLayout>
            {/* Simple outlet - let the routes handle their own logic */}
            <Outlet />
          </PageLayout>
          {(getPlatform() === DistributionPlatform.Samd ||
            getPlatform() === DistributionPlatform.Ldt) && (
            <UploadOverlayManager />
          )}
        </UploadProvider>

        {/* Floating Action Button for small screens */}
        {showCreateOrderButton && (
          <Tooltip title='Create Order'>
            <Fab
              component={Link}
              to={`/${organizationParam}/orders/new`}
              state={{
                background: {
                  pathname: location.pathname,
                  search: location.search,
                },
              }}
              color='primary'
              sx={{
                position: 'fixed',
                bottom: 24,
                right: 24,
                display: { xs: 'flex', sm: 'none' },
                zIndex: 1000,
              }}
            >
              <AddIcon />
            </Fab>
          </Tooltip>
        )}
      </MainLayout>
    </MainPageContainer>
  );
};

// Navigation Tabs Component - uses Link for nav items so middle-click / open in new tab work
const NavigationTabs: React.FC<{
  tabs: Array<{ id: number; label: string }>;
  organization: string;
  activeTab: number;
  onHelpTabClick: (event: React.MouseEvent<HTMLElement>) => void;
}> = ({ tabs, organization, activeTab, onHelpTabClick }) => {
  const getPathForTab = (tabId: number) => {
    switch (tabId) {
      case 0:
        return `/${organization}/orders`;
      case 1:
        return `/${organization}/members`;
      default:
        return undefined;
    }
  };

  return (
    <Tabs
      value={activeTab}
      aria-label='navigation tabs'
      sx={(theme) => ({
        height: APP_BAR_HEIGHT_PX,
        [theme.breakpoints.down('sm')]: {
          height: APP_BAR_HEIGHT_SM_PX,
        },
        '& .MuiTabs-indicator': {
          bottom: 0,
          height: 3,
        },
      })}
    >
      {tabs.map((tab, index) => {
        const path = getPathForTab(tab.id);
        if (path) {
          return (
            <Tab
              key={tab.id}
              component={Link}
              to={path}
              label={tab.label}
              value={index}
              sx={(theme) => ({
                textTransform: 'none',
                height: APP_BAR_HEIGHT_PX,
                [theme.breakpoints.down('sm')]: {
                  height: APP_BAR_HEIGHT_SM_PX,
                },
              })}
            />
          );
        }
        return (
          <Tab
            key={tab.id}
            onClick={onHelpTabClick}
            label={tab.label}
            value={index}
            sx={(theme) => ({
              textTransform: 'none',
              height: APP_BAR_HEIGHT_PX,
              [theme.breakpoints.down('sm')]: {
                height: APP_BAR_HEIGHT_SM_PX,
              },
            })}
          />
        );
      })}
    </Tabs>
  );
};

/**
 * RETIRED ANNOUNCEMENT KEY — do not reuse.
 * Was used to show a one-time popup about NCCN Risk Category and Gleason Scores
 * no longer being required. Removed after the announcement expired on May 15, 2026.
 * The key may still exist in some users' browsers as a residual localStorage entry.
 */
// const NCCN_GLEASON_ANNOUNCEMENT_KEY = 'hasShownNccnGleasonAnnouncement';

/**
 * RETIRED ANNOUNCEMENT KEY — do not reuse.
 * Was used to announce a new prostate test(mHSPC) that is expired on '2026-07-02', and is
 * no longer needed. Only shown to ordering physicians and clinic admins on the CLINIC workflow, one time per user.
 */
// export const PROSTATE_MHSPC_ANNOUNCEMENT_STORAGE_KEY = 'hasShownProstateMhspcAnnouncement
